import {
  Component, OnInit, OnDestroy, OnChanges, SimpleChanges,
  Input, Output, EventEmitter, HostListener, ViewChild, ElementRef,
} from '@angular/core';
import { VideoFeed } from '../../models/video-feed.model';
import { AuthenticationService } from '../services/authentication.service';
import { ApiConstants } from '../constants/api-constants';
import {
  createRsePlayerControls,
  RsePlayerControlsState,
  toggleRseMuted,
  toggleRseObjectFit,
} from '../rse-player/rse-player-controls';
import { RseZoomController } from '../rse-player/rse-player-zoom';

export type FullscreenMediaType = 'video' | 'photo' | 'stream';
export type FullscreenEmbedMode = 'stream' | 'webpage';

export interface FullscreenMediaPayload {
  type?: FullscreenMediaType;
  embedMode?: FullscreenEmbedMode;
  url: string | string[];
  description?: string;
  invalidUrl?: boolean;
}

type OverlayMedia = VideoFeed | FullscreenMediaPayload;
type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

@Component({
  selector: 'app-fullscreen-video',
  templateUrl: './fullscreen-video.component.html',
  styleUrls: ['./fullscreen-video.component.scss']
})
export class FullscreenVideoComponent implements OnInit, OnDestroy, OnChanges {
  @Input() videoFeed: OverlayMedia;
  @Input() isVisible: boolean = false;
  @Output() closeFullscreen = new EventEmitter<void>();

  widgetX: number = 100;
  widgetY: number = 100;
  widgetWidth: number = 800;
  widgetHeight: number = 600;
  /** Shared zoom/pan controller (same as <rse-player>). */
  readonly zoomCtrl = new RseZoomController();

  @ViewChild('zoomViewport') private zoomViewport?: ElementRef<HTMLElement>;

  private _originalX: number = 100;
  private _originalY: number = 100;
  private _originalWidth: number = 800;
  private _originalHeight: number = 600;
  private isMaximized: boolean = false;

  get originalX() { return this._originalX; }
  set originalX(value) { this._originalX = value; }

  get originalY() { return this._originalY; }
  set originalY(value) { this._originalY = value; }

  get originalWidth() { return this._originalWidth; }
  set originalWidth(value) { this._originalWidth = value; }

  get originalHeight() { return this._originalHeight; }
  set originalHeight(value) { this._originalHeight = value; }

  private isDragging: boolean = false;
  private isResizing: boolean = false;
  private dragOffsetX: number = 0;
  private dragOffsetY: number = 0;
  private resizeDirection: ResizeDirection | '' = '';
  private initialWidth: number = 0;
  private initialHeight: number = 0;
  private initialX: number = 0;
  private initialY: number = 0;
  private pointerStartX: number = 0;
  private pointerStartY: number = 0;
  private activePointerId: number | null = null;
  private captureTarget: HTMLElement | null = null;

  get resizingActive(): boolean {
    return this.isResizing;
  }

  get draggingActive(): boolean {
    return this.isDragging;
  }

  get isInteracting(): boolean {
    return this.isDragging || this.isResizing || this.zoomCtrl.isPanning;
  }

  get interactionCursor(): string {
    if (this.zoomCtrl.isPanning) {
      return 'grabbing';
    }
    if (this.isDragging) {
      return 'move';
    }
    return this.getResizeCursor(this.resizeDirection);
  }

  readonly streamAppToken = ApiConstants.STREAM_APP_TOKEN;
  playerUi: RsePlayerControlsState = createRsePlayerControls();

  get streamEngineServerUrl(): string {
    return this.authenticationService.getUser()?.streamServerIP || '';
  }

  constructor(private authenticationService: AuthenticationService) { }

  toggleMuted(event?: Event): void {
    toggleRseMuted(this.playerUi, event);
  }

  toggleObjectFit(event?: Event): void {
    toggleRseObjectFit(this.playerUi, event);
  }

  zoomIn(event?: Event): void {
    this.syncZoomViewport();
    this.zoomCtrl.zoomIn(event);
  }

  zoomOut(event?: Event): void {
    this.syncZoomViewport();
    this.zoomCtrl.zoomOut(event);
  }

  resetZoom(event?: Event): void {
    this.zoomCtrl.reset(event);
  }

  onMediaWheel(event: WheelEvent): void {
    if (!this.hasMedia) {
      return;
    }
    this.syncZoomViewport();
    this.zoomCtrl.onWheel(event);
  }

  startPan(event: PointerEvent): void {
    this.syncZoomViewport();
    this.zoomCtrl.startPan(event, { blocked: this.isDragging || this.isResizing });
  }

  private syncZoomViewport(): void {
    this.zoomCtrl.setViewport(this.zoomViewport?.nativeElement || null);
    this.zoomCtrl.setFallbackSize(this.widgetWidth, Math.max(0, this.widgetHeight - 40));
  }

  ngOnInit() {
    this.centerWidget();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['videoFeed'] || (changes['isVisible'] && this.isVisible)) {
      this.zoomCtrl.reset();
      this.playerUi = createRsePlayerControls();
    }
  }

  ngOnDestroy() {
    this.endInteraction();
  }

  @HostListener('window:blur')
  onWindowBlur() {
    this.endInteraction();
  }

  @HostListener('document:pointermove', ['$event'])
  onDocumentPointerMove(event: PointerEvent) {
    this.onPointerMove(event);
  }

  @HostListener('document:pointerup', ['$event'])
  @HostListener('document:pointercancel', ['$event'])
  onDocumentPointerUp(event: PointerEvent) {
    this.onPointerUp(event);
  }

  startDrag(event: PointerEvent) {
    const target = event.target as Element;
    if (target.closest('button') || target.closest('.widget-controls')) {
      return;
    }
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.isDragging = true;
    this.isResizing = false;
    this.resizeDirection = '';
    this.dragOffsetX = event.clientX - this.widgetX;
    this.dragOffsetY = event.clientY - this.widgetY;
    this.beginPointerSession(event);
  }

  startResize(event: PointerEvent, direction: ResizeDirection) {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.isResizing = true;
    this.isDragging = false;
    this.resizeDirection = direction;
    this.initialWidth = this.widgetWidth;
    this.initialHeight = this.widgetHeight;
    this.initialX = this.widgetX;
    this.initialY = this.widgetY;
    this.pointerStartX = event.clientX;
    this.pointerStartY = event.clientY;
    this.beginPointerSession(event);
  }

  onPointerMove(event: PointerEvent) {
    if (this.zoomCtrl.movePan(event)) {
      return;
    }

    if (!this.isDragging && !this.isResizing) {
      return;
    }
    if (event.pointerId !== this.activePointerId) {
      return;
    }

    event.preventDefault();

    if (this.isDragging) {
      this.applyDrag(event.clientX, event.clientY);
      return;
    }

    if (this.isResizing) {
      // Keep resize active only while the pointer stays near the active edge.
      // Outward movement along that edge is allowed (smooth enlarge).
      // Leaving the frame sideways / going deep inside cancels resize.
      if (!this.isPointerNearActiveEdge(event.clientX, event.clientY)) {
        this.endInteraction();
        return;
      }
      this.applyResize(event.clientX, event.clientY);
    }
  }

  /**
   * True while the pointer remains on/near the edge being resized.
   * Outward direction is unbounded so enlarge stays smooth; lateral drift
   * away from the frame (or deep into the content) ends the resize.
   */
  private isPointerNearActiveEdge(clientX: number, clientY: number): boolean {
    const band = 24;
    const left = this.widgetX;
    const top = this.widgetY;
    const right = this.widgetX + this.widgetWidth;
    const bottom = this.widgetY + this.widgetHeight;

    const withinFrameX = clientX >= left - band && clientX <= right + band;
    const withinFrameY = clientY >= top - band && clientY <= bottom + band;

    switch (this.resizeDirection) {
      case 's':
        return withinFrameX && clientY >= bottom - band;
      case 'n':
        return withinFrameX && clientY <= top + band;
      case 'e':
        return withinFrameY && clientX >= right - band;
      case 'w':
        return withinFrameY && clientX <= left + band;
      case 'se':
        return clientX >= right - band && clientY >= bottom - band;
      case 'sw':
        return clientX <= left + band && clientY >= bottom - band;
      case 'ne':
        return clientX >= right - band && clientY <= top + band;
      case 'nw':
        return clientX <= left + band && clientY <= top + band;
      default:
        return false;
    }
  }

  onPointerUp(event: PointerEvent) {
    if (this.zoomCtrl.endPan(event)) {
      return;
    }
    if (this.activePointerId !== null && event.pointerId !== this.activePointerId) {
      return;
    }
    this.endInteraction();
  }

  private beginPointerSession(event: PointerEvent) {
    this.activePointerId = event.pointerId;
    this.captureTarget = event.currentTarget as HTMLElement;

    try {
      this.captureTarget?.setPointerCapture(event.pointerId);
    } catch {
      // Some browsers may reject capture while pointer is already released.
    }

    document.body.style.userSelect = 'none';
  }

  private endInteraction() {
    if (this.captureTarget && this.activePointerId !== null) {
      try {
        if (this.captureTarget.hasPointerCapture?.(this.activePointerId)) {
          this.captureTarget.releasePointerCapture(this.activePointerId);
        }
      } catch {
        // Ignore release failures during teardown.
      }
    }

    const wasResizing = this.isResizing;

    this.isDragging = false;
    this.isResizing = false;
    this.resizeDirection = '';
    this.activePointerId = null;
    this.captureTarget = null;
    this.zoomCtrl.endPan();
    document.body.style.userSelect = '';

    if (wasResizing && !this.isMaximized) {
      this.originalX = this.widgetX;
      this.originalY = this.widgetY;
      this.originalWidth = this.widgetWidth;
      this.originalHeight = this.widgetHeight;
    }
  }

  private applyDrag(clientX: number, clientY: number) {
    const newX = clientX - this.dragOffsetX;
    const newY = clientY - this.dragOffsetY;
    const maxX = window.innerWidth - this.widgetWidth;
    const maxY = window.innerHeight - this.widgetHeight;

    this.widgetX = Math.max(0, Math.min(newX, maxX));
    this.widgetY = Math.max(0, Math.min(newY, maxY));
  }

  private applyResize(clientX: number, clientY: number) {
    const deltaX = clientX - this.pointerStartX;
    const deltaY = clientY - this.pointerStartY;

    const minWidth = 300;
    const minHeight = 200;
    const maxWidth = window.innerWidth;
    const maxHeight = window.innerHeight;

    let newWidth = this.initialWidth;
    let newHeight = this.initialHeight;
    let newX = this.initialX;
    let newY = this.initialY;

    switch (this.resizeDirection) {
      case 'n':
        newHeight = Math.max(minHeight, Math.min(maxHeight, this.initialHeight - deltaY));
        newY = this.initialY + (this.initialHeight - newHeight);
        break;
      case 's':
        newHeight = Math.max(minHeight, Math.min(maxHeight, this.initialHeight + deltaY));
        break;
      case 'e':
        newWidth = Math.max(minWidth, Math.min(maxWidth, this.initialWidth + deltaX));
        break;
      case 'w':
        newWidth = Math.max(minWidth, Math.min(maxWidth, this.initialWidth - deltaX));
        newX = this.initialX + (this.initialWidth - newWidth);
        break;
      case 'ne':
        newHeight = Math.max(minHeight, Math.min(maxHeight, this.initialHeight - deltaY));
        newWidth = Math.max(minWidth, Math.min(maxWidth, this.initialWidth + deltaX));
        newY = this.initialY + (this.initialHeight - newHeight);
        break;
      case 'nw':
        newHeight = Math.max(minHeight, Math.min(maxHeight, this.initialHeight - deltaY));
        newWidth = Math.max(minWidth, Math.min(maxWidth, this.initialWidth - deltaX));
        newY = this.initialY + (this.initialHeight - newHeight);
        newX = this.initialX + (this.initialWidth - newWidth);
        break;
      case 'se':
        newHeight = Math.max(minHeight, Math.min(maxHeight, this.initialHeight + deltaY));
        newWidth = Math.max(minWidth, Math.min(maxWidth, this.initialWidth + deltaX));
        break;
      case 'sw':
        newHeight = Math.max(minHeight, Math.min(maxHeight, this.initialHeight + deltaY));
        newWidth = Math.max(minWidth, Math.min(maxWidth, this.initialWidth - deltaX));
        newX = this.initialX + (this.initialWidth - newWidth);
        break;
    }

    if (newX + newWidth > window.innerWidth) {
      newWidth = window.innerWidth - newX;
    }
    if (newY + newHeight > window.innerHeight) {
      newHeight = window.innerHeight - newY;
    }
    if (newX < 0) {
      newWidth += newX;
      newX = 0;
    }
    if (newY < 0) {
      newHeight += newY;
      newY = 0;
    }

    this.widgetWidth = Math.max(minWidth, newWidth);
    this.widgetHeight = Math.max(minHeight, newHeight);
    this.widgetX = newX;
    this.widgetY = newY;
  }

  private getResizeCursor(direction: ResizeDirection | ''): string {
    switch (direction) {
      case 'n': return 'n-resize';
      case 's': return 's-resize';
      case 'e': return 'e-resize';
      case 'w': return 'w-resize';
      case 'ne': return 'ne-resize';
      case 'nw': return 'nw-resize';
      case 'se': return 'se-resize';
      case 'sw': return 'sw-resize';
      default: return 'default';
    }
  }

  centerWidget() {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    this.widgetX = (screenWidth - this.widgetWidth) / 2;
    this.widgetY = (screenHeight - this.widgetHeight) / 2;
  }

  minimizeWidget() {
    if (this.isMaximized) {
      this.restoreFromMaximized();
    }

    this.originalX = this.widgetX;
    this.originalY = this.widgetY;
    this.originalWidth = this.widgetWidth;
    this.originalHeight = this.widgetHeight;

    this.widgetWidth = 400;
    this.widgetHeight = 300;
  }

  maximizeWidget() {
    if (!this.isMaximized) {
      this.originalX = this.widgetX;
      this.originalY = this.widgetY;
      this.originalWidth = this.widgetWidth;
      this.originalHeight = this.widgetHeight;

      this.widgetX = 0;
      this.widgetY = 0;
      this.widgetWidth = window.innerWidth;
      this.widgetHeight = window.innerHeight;
      this.isMaximized = true;
    } else {
      this.restoreFromMaximized();
    }
  }

  private restoreFromMaximized() {
    this.widgetX = this.originalX;
    this.widgetY = this.originalY;
    this.widgetWidth = this.originalWidth;
    this.widgetHeight = this.originalHeight;
    this.isMaximized = false;
  }

  closeVideo() {
    this.endInteraction();
    this.resetZoom();
    this.closeFullscreen.emit();
  }

  get mediaType(): FullscreenMediaType {
    const payload = this.videoFeed as FullscreenMediaPayload;
    if (payload?.type) {
      return payload.type;
    }
    return 'stream';
  }

  get mediaUrl(): string | null {
    if (!this.videoFeed) {
      return null;
    }

    const urlValue = (this.videoFeed as any).url;
    if (Array.isArray(urlValue)) {
      return urlValue.length ? urlValue[0] : null;
    }
    return urlValue || null;
  }

  get isStream(): boolean {
    return this.mediaType === 'stream';
  }

  get isVideo(): boolean {
    return this.mediaType === 'video';
  }

  get isPhoto(): boolean {
    return this.mediaType === 'photo';
  }

  get hasMedia(): boolean {
    return !!this.mediaUrl;
  }

  get isExternalWebPage(): boolean {
    const feed = this.videoFeed as FullscreenMediaPayload & VideoFeed;
    if (!feed || !this.isStream) {
      return false;
    }
    if (feed.embedMode === 'webpage') {
      return true;
    }
    if (feed.embedMode === 'stream') {
      return false;
    }
    if (feed.branch || feed.ip || feed.name) {
      return false;
    }
    const urlValue = feed.url;
    return typeof urlValue === 'string' && /^https?:\/\//i.test(urlValue);
  }
}
